import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import { AppBar, Avatar, Divider, Drawer, Grid, IconButton, Typography, useTheme, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { set as IndexedDB_set } from 'idb-keyval';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth0 } from "@auth0/auth0-react";
import { signOut } from "./logout";

import { DataAPIContext, GET_ONE } from '../api';
import { useBreakpoint } from '../components/breakpoints';
import { imageURL } from '../components/images';
import { usePageVisibility } from '../components/usePageVisibility';
import Dashboard from '../dashboard';
import { ReactComponent as SupportIcon } from '../icons/main_menu/support.svg';
import MuiXLicense from '../settings/MuiXLicense';
import sharedContext from '../sharedContext';
import { ContextStore } from '../store/contextStore';
import { isDashboardChromeEmbed } from '../chromeEmbed';
import { subscribeVisualViewport } from './visualViewport';
import { GMapContext, WindowNamespace } from '../utils';
import { getTourOperatorLogoUrl } from '../utils/pwaBranding';
import { BackgroundTaskProvider, TaskNotificationBadge, TaskPopoverWidget, TaskFullScreenDialog } from '../bg_tasks';
import { LandingHold, ONBOARDING_PATH, OnboardingBanner, useOnboardingLanding } from '../onboarding';
import DashboardMenu from './menu';
import { COLLAPSED_DRAWER_WIDTH, DRAWER_WIDTH } from './drawer';
import LicenseeSelector from './LicenseeSelector';
import UserMenu from './UserMenu';
import EnvironmentBadge, { EnvironmentSwitch } from './EnvironmentBadge';
import SandboxBanner from '../developer/SandboxBanner';
import { cv } from '../themeVars';
import { refreshMfaStatus } from '../account/securityApi';

const drawerWidth = DRAWER_WIDTH;
const collapsedDrawerWidth = COLLAPSED_DRAWER_WIDTH;

// bootstrap.mfa is written at sign-in, so a session older than dashboard MFA (or than its backend flag) would
// never show the user menu's "Sign-in security". Refreshed once per page load: every route mounts its own
// MainLayout, and a 404 (the feature off) every navigation would be noise.
let _mfaStatusRefreshed = false;

const useStyles = makeStyles((theme) => ({
    toolbarIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: '0 8px',
        ...theme.mixins.toolbar,
    },
    appBar: {
        backgroundColor: `${cv('surface', '#fff')} !important`,
        color: `${cv('text-secondary', '#747475')} !important`,
        borderBottom: `1px solid ${cv('border', '#d8d8d8')}`,
    },

    menuButton: {
        marginRight: '46px !important',
    },
    menuButtonHidden: {
        display: 'none',
    },
    title: {
        flexGrow: 1,
    },
    drawerPaper: {
        position: 'relative',
        border: 0,
        whiteSpace: 'nowrap',
        width: drawerWidth,
        transition: 'all 250ms ease !important',
        zIndex: '10001 !important',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    drawerPaperClose: {
        overflowX: 'hidden',
        transition: 'all 200ms ease !important',
        zIndex: '10001 !important',
        width: collapsedDrawerWidth,
    },
    appBarSpacer: theme.mixins.toolbar,
    content: {
        flexGrow: 1,
        height: '99%',
        overflow: 'inherit',
        position: 'relative',
        [theme.breakpoints.down('sm')]: {
            // auto, not hidden: phone chrome + keyboard used to clip the chat
            // composer and list pagination with no way to scroll to them.
            overflowY: 'auto',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        },
        transition: 'all 250ms ease',
        zIndex: '1 !important',
        // zIndex: 'auto',
    },
    container: {
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
    },
    paper: {
        padding: theme.spacing(2),
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
    },
    fixedHeight: {
        height: 240,
    },
}));

const getUserAvatar = (base, user) => {
    if (!user) {
        return null;
    }

    const hasAvatar = user.avatar && user.avatar.p && user.avatar.s;
    const avatarURL = hasAvatar ? imageURL(base, user.avatar, 'cover_30x30') : null;

    return (
        <Avatar alt={user.display_name} src={avatarURL || undefined}>
            {user.first_name?.charAt(0) ?? 'A'}
            {user.last_name?.charAt(0) ?? ''}
        </Avatar>
    );
};

const getTourOperatorLogo = (base, top) => {
    if (!top) {
        return null;
    }

    const toplogoURL = imageURL(base, top.logo, 'x30');
    return toplogoURL ? <img src={toplogoURL} alt={top?.name} /> : <Avatar>{top.name?.charAt(0)}</Avatar>;
};

// const hiddenDrawerLocationList = [
//     /^\/trip\/\d+/,
// ]
// const locationWithHiddenDrawer = (loc) => {
//     return hiddenDrawerLocationList.filter(f => f.test(loc.pathname)).length > 0;
//     // return false;
// }

const MainLayout = ({ component, ...props }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { logout: auth0logout } = useAuth0();

    const history = useHistory();
    const classes = useStyles();
    const contextStoreData = React.useContext(ContextStore);
    const { tourOperator, user, settings, logout, hasPerm, currentLicensee, auth_method } = React.useContext(ContextStore);
    const isPageVisible = usePageVisibility();
    const permRefreshTm = React.useRef(null);
    const bgTaskAnchorRef = React.useRef(null);

    const top = tourOperator;
    const userAvatar = getUserAvatar(settings?.images?.base, user);
    const topLogo = getTourOperatorLogo(settings?.images?.base, top);
    const theme = useTheme();

    // Default useIsWidthUp hook does not work here since it only detects small screens after initial render
    const isSmallScreen = window.innerWidth < 500;
    const ChildComponent = component || Dashboard.List;

    // Subscribes to breakpoint changes so the layout re-renders on resize and
    // isSmallScreen (read from window.innerWidth above) stays current.
    useBreakpoint();

    // Used for the background push notification logo. The home-screen icon and manifest are set
    // by public/index.html (CP-5504).
    React.useEffect(() => {
        const logoUrl = getTourOperatorLogoUrl(top, settings, 'x192');
        if (logoUrl) {
            IndexedDB_set('topLogoURL', logoUrl);
        }
    }, [top?.logo?.p, top?.logo?.s, settings?.images?.base]);

    // Sync notification sound settings from tour operator config to IndexedDB for service worker
    React.useEffect(() => {
        if (top?.config?.background_dashboard_chat_notification_sound_enabled !== undefined) {
            const soundEnabled = !!top.config.background_dashboard_chat_notification_sound_enabled;
            IndexedDB_set('notificationSoundEnabledBackground', soundEnabled);
        }
        if (top?.config?.foreground_dashboard_chat_notification_sound_enabled !== undefined) {
            const soundEnabled = !!top.config.foreground_dashboard_chat_notification_sound_enabled;
            IndexedDB_set('notificationSoundEnabledForeground', soundEnabled);
        }
    }, [top?.config?.background_dashboard_chat_notification_sound_enabled, top?.config?.foreground_dashboard_chat_notification_sound_enabled]);
    // React.useEffect(() => {
    //     // const timer = setTimeout(() => dispatch(rotateToken(currentToken)), nextTokenRefreshDelay);
    //     const timer = setTimeout(() => rotateToken(currentToken), nextTokenRefreshDelay);
    //     // console.log(`token refresh scheduled in ${(nextTokenRefreshDelay/1000).toFixed(2)}s`)
    //     return () => clearTimeout(timer);
    // });

    const { setMfaStatus } = contextStoreData;
    React.useEffect(() => {
        if (_mfaStatusRefreshed || !dataAPI || !setMfaStatus) {
            return;
        }
        _mfaStatusRefreshed = true;
        refreshMfaStatus(dataAPI, setMfaStatus);
    }, [dataAPI, setMfaStatus]);

    // hide menu on xs screens by default, show on others
    const [isDrawerOpen, setIsDrawerOpen] = React.useState(!isSmallScreen);

    React.useEffect(() => subscribeVisualViewport(), []);
    const [isAutoCollapse, setIsAutoCollapse] = React.useState(false);
    const [isLoggingOut, setIsLoggingOut] = React.useState(false);

    const handleDrawerToggle = () => {
        if (isAutoCollapse) {
            setIsDrawerOpen(true);
            setIsAutoCollapse((prev) => !prev);
            return;
        }
        setIsDrawerOpen(!isDrawerOpen);
        setIsAutoCollapse((prev) => !prev);
    };

    React.useEffect(() => {
        const pollingFn = () => {
            // The session this request is sent for. A response that lands after the session
            // changed ("Log in as", sign-out, a token rotation) describes the previous token's
            // user: storing it would give the new user someone else's menu.
            const sentToken = contextStoreData.token;
            dataAPI(GET_ONE, 'user', { urlSuffix: 'my_permissions/' }).then((res) => {
                // console.log('user perms', res);
                if (sharedContext.get('bootstrap')?.token !== sentToken) {
                    return;
                }
                if (res.statusCode === 200) {
                    if (res.data && res.data.cache) {
                        const newPerms = JSON.stringify(res.data.cache);
                        const oldPerms = JSON.stringify(contextStoreData.user.permissions);
                        if (newPerms === oldPerms) {
                            // console.log('user perms: no change detected since last time', res.data.cache, contextStoreData.user.permissions);
                        } else {
                            console.log('audit: user perms: change detected, refreshing local cache', res.data.cache, contextStoreData.user.permissions);
                            contextStoreData.setUser((prevState) => ({
                                ...prevState,
                                permissions: res.data.cache,
                            }));
                        }
                    }
                    // Licensees used to be written once at login and never refreshed, so a
                    // licensee added (or newly granted) mid-session stayed missing from the
                    // sidebar selector and every form's Licensee field until a re-login.
                    // undefined = backend without the franchise block: leave what we have.
                    if (res.data && res.data.franchise !== undefined) {
                        const newFranchise = JSON.stringify(res.data.franchise ?? null);
                        const oldFranchise = JSON.stringify(contextStoreData.franchiseData ?? null);
                        if (newFranchise !== oldFranchise) {
                            console.log('audit: licensees: change detected, refreshing local cache', res.data.franchise, contextStoreData.franchiseData);
                            contextStoreData.setFranchiseData(res.data.franchise);
                            // A selected licensee the user just lost access to would otherwise
                            // keep being injected as franchise_to on every request.
                            const stillVisible = (res.data.franchise?.licensees || []).some(
                                (l) => l.id === contextStoreData.currentLicensee,
                            );
                            if (contextStoreData.currentLicensee != null && !stillVisible) {
                                contextStoreData.setCurrentLicensee(null);
                            }
                        }
                    }
                } else {
                    console.error('user perms: error fetching permissions cache', res);
                }
            });
        };

        const startPolling = () => {
            permRefreshTm.current = setInterval(pollingFn, 30000 + Math.round(Math.random() * 20000));
        }; // refresh each 30-50 seconds
        const stopPolling = () => {
            permRefreshTm.current && clearInterval(permRefreshTm.current);
        };

        if (isPageVisible) {
            pollingFn();
            startPolling();
        } else {
            stopPolling();
        }

        return () => {
            stopPolling();
        };
    }, [isPageVisible, dataAPI, contextStoreData]);

    // const fixedHeightPaper = clsx(classes.paper, classes.fixedHeight);

    // const childrenList = children.constructor !== Array ? [ children ] : children;
    // const menuItems = (childrenList || []).map((c, idx) => (
    //     <ListItemLink primary={c.props.primary || "Menu Link"} key={`menuitem-${idx}`}  icon={c.props.icon || <DashboardIcon/>} to={c.props.to || "/"}/>
    // ));
    // const routeList = (childrenList || []).map((c, idx) => (
    //     <Route exact={c.props.exact || false} path={c.props.to} key={`route-${idx}`} children={c.props.children}/>
    // ));

    const handleLogout = (ev) => {
        // Set logout flag to prevent auto-redirect to chat
        setIsLoggingOut(true);
        
        // Logout immediately without URL change; Auth0 only on an Auth0 tenant (./logout).
        signOut({ authMethod: auth_method, logout, auth0Logout: auth0logout });
    };
    const gmapNs = WindowNamespace();

    const api = {
        closeDrawer: () => setIsDrawerOpen(false),
        openDrawer: () => setIsDrawerOpen(true),
        isDrawerOpen: () => isDrawerOpen,
    };

    // Some browsers require a user action in order to show the notification permission prompt.
    // So attching a click handler to the page to ensure it gets shown anytime a user clicks
    // (but only if they haven't aleady allowed/blocked notifications)
    // Skip in Chrome extension side-panel embed (?cp_embed=1) to avoid permission prompts / OS toasts.
    React.useEffect(() => {
        if (isDashboardChromeEmbed()) {
            return undefined;
        }
        // https://stackoverflow.com/questions/10444077/javascript-removeeventlistener-not-working
        document.body.addEventListener('click', getNotificationPermission, { once: true });

        return function cleanup() {
            window.removeEventListener('click', getNotificationPermission, true);
        };
    }, []);

    const getNotificationPermission = React.useCallback(() => {
        // iOS browsers have no Notification API outside a Home Screen web app.
        if (typeof Notification === 'undefined') return;
        Notification.requestPermission().then(function (permission) {
            console.log('main: Notifications permission:', permission);
        });
    });

    const handleOnSupportClick = () => {
        if (isSmallScreen) {
            setIsDrawerOpen(false);
        }
        history.push('/dashboard');
    };

    const handleOnTOLogoClickOnMobile = () => {
        setIsDrawerOpen(false);
        history.push('/');
        return;
    };

    // While the onboarding is open, an admin's first landing of the page load (every sign-in reloads
    // the app on "/") is the Onboarding page; `hold` while that is being read, so Chat does not flash.
    const onboardingLanding = useOnboardingLanding(history.location.pathname);
    React.useLayoutEffect(() => {
        if (onboardingLanding === 'onboarding' && !isLoggingOut) {
            history.replace(ONBOARDING_PATH);
        }
    }, [onboardingLanding, isLoggingOut, history]);

    if (history.location.pathname === '/' && hasPerm('chat', 'list') && !isLoggingOut && onboardingLanding === 'none') {
        history.push('/chat');
    }

    return (
        <BackgroundTaskProvider>
        <GMapContext.Provider value={gmapNs}>
            <div className={classes.root}>
                <MuiXLicense />
                <TaskPopoverWidget anchorEl={bgTaskAnchorRef.current} />
                <TaskFullScreenDialog />
                {isSmallScreen && (
                    <AppBar position="fixed" id="app-bar" elevation={0} className={clsx(classes.appBar)}>
                        <Grid container justifyContent="space-between" p={1} pl={2} spacing={1} style={{ opacity: isDrawerOpen ? 0 : 1, transition: !isDrawerOpen ? 'opacity 200ms ease-out' : 'opacity 0ms ease-out', transitionDelay: !isDrawerOpen ? '100ms' : '0ms' }}>
                            <Grid item xs={10} container justifyContent="flex-start" alignItems="center">
                                <Grid item>
                                    <IconButton edge="start" color="inherit" aria-label="open drawer" onClick={() => history.push('/')} style={{}}>
                                        {topLogo && topLogo}
                                    </IconButton>
                                </Grid>

                                <Grid item>
                                    <Typography component="h1" variant="h6" color="primary" noWrap>
                                        {top && top.name}
                                    </Typography>
                                </Grid>
                                <Grid item pl={1}>
                                    <EnvironmentBadge />
                                </Grid>
                            </Grid>

                            <Grid item xs={2} container justifyContent="flex-end" alignItems="center">
                                <IconButton onClick={handleDrawerToggle}>
                                    <MenuIcon />
                                </IconButton>
                            </Grid>
                        </Grid>
                    </AppBar>
                )}

                <Drawer
                    variant={isSmallScreen ? 'temporary' : 'permanent'}
                    anchor="left"
                    classes={{ paper: clsx(classes.drawerPaper, !isDrawerOpen && classes.drawerPaperClose) }}
                    //Needed to change the zIndex to make sure menu is always on top e.g buttonBox on EditItinerary
                    style={{ zIndex: 10000 }}
                    open={isDrawerOpen}
                    onClose={(event, reason) => {
                        if (reason === 'backdropClick' && isAutoCollapse) {
                            setIsDrawerOpen(false);
                        }
                    }}
                    onMouseEnter={() => (isAutoCollapse ? setIsDrawerOpen(true) : console.log(''))}
                    onMouseLeave={() => (isAutoCollapse ? setIsDrawerOpen(false) : console.log(''))}
                >
                    <Grid container justifyContent={isSmallScreen ? 'space-between' : 'center'} alignItems="center" p={1} pl={2} style={{ marginBottom: '-0.5rem' }}>
                        <Grid item xs={10} sm={12} container alignItems="center">
                            <Grid item pl={isDrawerOpen ? 0 : 0.5}>
                                <IconButton edge="start" color="inherit" aria-label="open drawer" onClick={() => (isSmallScreen ? handleOnTOLogoClickOnMobile() : handleDrawerToggle())}>
                                    {topLogo && topLogo}
                                </IconButton>
                            </Grid>
                            <Tooltip title={top && top.name} PopperProps={{ style: {zIndex:99999 } }}>
                                <Grid item>
                                    <Typography
                                        style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: isDrawerOpen ? '140px' : '0px', transition: isDrawerOpen ? 'max-width 300ms ease-out' : 'max-width 0ms ease-out', transitionDelay: isDrawerOpen ? '150ms' : '0ms' }}
                                        component="h1"
                                        variant="h6"
                                        color="primary"
                                    >
                                        {top && top.name}
                                    </Typography>
                                </Grid>
                            </Tooltip>
                        </Grid>

                        {isSmallScreen && (
                            <Grid item xs={2} container justifyContent="flex-end">
                                <IconButton onClick={() => setIsDrawerOpen(false)}>
                                    <CloseIcon />
                                </IconButton>
                            </Grid>
                        )}
                    </Grid>
                    {/* The sandbox badge, and the Production <-> Sandbox switch (DSEC D3): nothing for most production users. */}
                    <EnvironmentSwitch compact={!isDrawerOpen} />
                    <LicenseeSelector isDrawerOpen={isDrawerOpen} />
                    <DashboardMenu isDrawerOpen={isDrawerOpen} setIsDrawerOpen={setIsDrawerOpen} isSmallScreen={isSmallScreen} />
                    <Grid
                        item
                        container
                        alignItems="center"
                        position="fixed"
                        bottom={0}
                        width={!isSmallScreen ? (isDrawerOpen ? drawerWidth : collapsedDrawerWidth) : '100%'}
                        spacing={1}
                        justifyContent="space-between"
                        xs={12}
                        p={isSmallScreen ? 2 : 1}
                        pr={1}
                        pl={1.75}
                        style={{ backgroundColor: theme.palette.background.paper }}
                    >
                        <Grid item container pt={2} spacing={1} xs={12} alignItems="center">
                            <Grid item container spacing={1} xs style={{ cursor: 'pointer', paddingLeft: isDrawerOpen ? '0.5rem' : '0.9rem' }} onClick={handleOnSupportClick}>
                                <Grid item>
                                    <SupportIcon />
                                </Grid>
                                {isDrawerOpen && (
                                    <Grid item>
                                        <Typography>Support</Typography>
                                    </Grid>
                                )}
                            </Grid>
                            {!isSmallScreen && (
                                <Grid item ref={bgTaskAnchorRef}>
                                    <TaskNotificationBadge />
                                </Grid>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <Divider />
                        </Grid>
                        <Grid xs={9} item container spacing={1} justifyContent="flex-start" alignItems="center" style={{ paddingLeft: '2rem !important' }}>
                            <Grid item>
                                <UserMenu user={user} avatar={userAvatar} onNavigate={() => isSmallScreen && setIsDrawerOpen(false)} />
                            </Grid>
                            <Grid item>
                                {/* <Typography
                                    style={{
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: isDrawerOpen ? '140px' : '0px',
                                        transition: isDrawerOpen ? 'max-width 300ms ease-out' : 'max-width 0ms ease-out',
                                        transitionDelay: isDrawerOpen ? '150ms' : '0ms',
                                        height: isDrawerOpen ? '100%' : 0,
                                        cursor: 'pointer',
                                    }}
                                    onClick={(ev) => {
                                        history.push(`/user/${user.id}`);
                                    }}
                                >
                                    {user.display_name || user.email}
                                </Typography> */}
                            </Grid>
                        </Grid>
                        {isDrawerOpen && (
                            <Grid item onClick={handleLogout} style={{ cursor: 'pointer' }} container={isDrawerOpen ? false : true} justifyContent="center">
                                <img src={'/logout.svg'} alt="logout"></img>
                            </Grid>
                        )}
                    </Grid>
                </Drawer>
                <main key={currentLicensee ?? 'all'} className={classes.content} style={{ marginLeft: isDrawerOpen ? !isSmallScreen && drawerWidth : !isSmallScreen && theme.spacing(8) }}>
                    {isSmallScreen && <div className={classes.appBarSpacer} />}
                    <SandboxBanner />
                    <OnboardingBanner />
                    {onboardingLanding === 'none' ? <ChildComponent api={api} /> : onboardingLanding === 'hold' ? <LandingHold /> : null}
                </main>
            </div>
        </GMapContext.Provider>
        </BackgroundTaskProvider>
    );
};

export default MainLayout;
